import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "./ui/command";
import { api } from "../lib/api";
import LogoImage from "./LogoImage";

export default function SearchModal({ open, onClose, onPick }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState({ groups: [], total: 0 });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) {
            setQuery("");
            setResults({ groups: [], total: 0 });
            return;
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const trimmed = query.trim();
        if (!trimmed) {
            setResults({ groups: [], total: 0 });
            return;
        }
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const data = await api.search(trimmed);
                setResults(data);
            } catch {
                setResults({ groups: [], total: 0 });
            } finally {
                setLoading(false);
            }
        }, 180);
        return () => clearTimeout(t);
    }, [query, open]);

    return (
        <CommandDialog open={open} onOpenChange={(v) => !v && onClose()} title="Search BharatStack" description="Search across 331+ Indian products in 61 categories">
            <CommandInput
                value={query}
                onValueChange={setQuery}
                placeholder="Search Razorpay, Zoho, JioCinema..."
                data-testid="search-modal-input"
            />
            <CommandList data-testid="search-modal-list">
                {!query.trim() && (
                    <div className="px-4 py-8 text-center text-sm text-ink-muted">
                        <Search className="mx-auto h-6 w-6 text-saffron mb-2" />
                        Search across <strong>331+ products</strong> in{" "}
                        <strong>61 categories</strong>.
                        <div className="mt-3 flex flex-wrap gap-2 justify-center">
                            {[
                                "Payment",
                                "Cloud",
                                "Email",
                                "Video",
                                "AI",
                            ].map((tip) => (
                                <button
                                    key={tip}
                                    onClick={() => setQuery(tip)}
                                    className="text-xs px-2.5 py-1 rounded-full bg-saffron-50 text-saffron-700 border border-saffron-200 hover:bg-saffron-100"
                                >
                                    {tip}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {query.trim() && !loading && results.total === 0 && (
                    <CommandEmpty>
                        No results for "{query}". Try "payments" or "cloud".
                    </CommandEmpty>
                )}
                {results.groups.map((group, idx) => (
                    <React.Fragment key={group.category_slug}>
                        {idx > 0 && <CommandSeparator />}
                        <CommandGroup heading={group.category_name}>
                            {group.products.slice(0, 8).map((p) => (
                                <CommandItem
                                    key={p.id}
                                    value={`${p.name} ${p.category_name}`}
                                    onSelect={() => onPick(p)}
                                    className="gap-3"
                                    data-testid={`search-result-${p.slug}`}
                                >
                                    <LogoImage
                                        src={p.logo_url}
                                        domain={p.domain}
                                        name={p.name}
                                        size={28}
                                        rounded="rounded-md"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium text-ink truncate">
                                            {p.name}
                                        </div>
                                        <div className="text-xs text-ink-muted truncate">
                                            {p.domain}
                                        </div>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </React.Fragment>
                ))}
            </CommandList>
        </CommandDialog>
    );
}
